import React, { useEffect, useRef, useState } from "react";
import * as styled from "./ScheduleGrid.styled";
import { LuX } from "react-icons/lu";

const { lessonColors } = styled;

// Тип уроку → колір картки
const colorOf = (type) =>
  type === "Trial"
    ? lessonColors.trial
    : type === "Reschedule Lesson"
    ? lessonColors.reschedule
    : type === "Substitute"
    ? lessonColors.substitute
    : type === "cancel"
    ? lessonColors.cancel
    : lessonColors.regular;

const legend = [
  { label: "Урок", c: lessonColors.regular },
  { label: "Пробний", c: lessonColors.trial },
  { label: "Перенесення", c: lessonColors.reschedule },
  { label: "Заміна", c: lessonColors.substitute },
  { label: "Скасовано", c: lessonColors.cancel },
  { label: "Вільне вікно", c: lessonColors.slot },
];

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function ScheduleGrid({
  daysOfWeek,
  lessons,              // масив { day, hour, minute, name, time, type }
  handleLessonClick,
  resheduleLesson,
  setSelectedCell,
  setSelectedLesson,
  teacher,
  onDeleteSlot,
  currentWeek
}) {
  const [interval, setInterval] = useState(60); // 60 або 30 хв
  const scrollerRef = useRef(null);
  const scrolledRef = useRef(false);

  // Генеруємо timeSlots залежно від інтервалу
  const timeSlots = Array.from(
    { length: (24 * 60) / interval },
    (_, idx) => {
      const totalMin = idx * interval;
      const hour = Math.floor(totalMin / 60);
      const minute = totalMin % 60;
      const label = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      return { hour, minute, label };
    }
  );

  // Дати днів обраного тижня (для шапки й підсвітки «сьогодні»)
  const today = new Date();
  const dates = daysOfWeek.map((_, i) => {
    if (!currentWeek) return null;
    const d = new Date(currentWeek);
    d.setDate(d.getDate() + i);
    return d;
  });

  // Одразу прокручуємо до першого уроку тижня (або до 08:00), а не до півночі
  useEffect(() => {
    if (scrolledRef.current || !scrollerRef.current) return;
    const hours = lessons.map((lesson) => lesson.hour).filter((h) => typeof h === "number");
    const first = hours.length ? Math.max(0, Math.min(...hours) - 1) : 8;
    const row = scrollerRef.current.querySelector(`[data-hour="${first}"]`);
    if (row) {
      const head = scrollerRef.current.querySelector("[data-day-head]");
      const box = scrollerRef.current;
      box.scrollTop += row.getBoundingClientRect().top - box.getBoundingClientRect().top - (head?.offsetHeight || 0);
      scrolledRef.current = true;
    }
  }, [lessons]);

  const dayNameMapping = {
    Пн: "monday",
    Вт: "tuesday",
    Ср: "wednesday",
    Чт: "thursday",
    Пт: "friday",
    Сб: "saturday",
    Нд: "sunday"
  };

  // Знайти слот (розкладку) викладача
  const getSlotInfo = (dayIdx, slot) => {
    const rusDay = daysOfWeek[dayIdx];
    const engDay = dayNameMapping[rusDay];
    return teacher.slots?.find(s =>
      s.day === engDay && s.time === slot.label
    );
  };

  const handleCellClick = (dayIdx, slot) => {
    setSelectedCell({ day: dayIdx, hour: slot.hour, minute: slot.minute });
    resheduleLesson(dayIdx, slot.hour, slot.minute);
  };

  const handleDeleteSlot = (dayIdx, slot, e) => {
    e.stopPropagation();
    const info = getSlotInfo(dayIdx, slot);
    if (info && onDeleteSlot) onDeleteSlot(info);
  };

  return (
    <styled.Card>
      <styled.Toolbar>
        <styled.Legend>
          {legend.map(({ label, c }) => (
            <styled.LegendItem key={label} $c={c}>{label}</styled.LegendItem>
          ))}
        </styled.Legend>
        {/* Перемикач інтервалу */}
        <styled.Segment>
          <styled.SegmentBtn $active={interval === 60} onClick={() => setInterval(60)}>
            60 хв
          </styled.SegmentBtn>
          <styled.SegmentBtn $active={interval === 30} onClick={() => setInterval(30)}>
            30 хв
          </styled.SegmentBtn>
        </styled.Segment>
      </styled.Toolbar>

      <styled.Scroller ref={scrollerRef}>
        <styled.ScheduleGrid>
          {/* Заголовки днів */}
          <styled.GridHour>
            <styled.Corner />
            {daysOfWeek.map((d, i) => (
              <styled.DayHead key={i} data-day-head $today={dates[i] && sameDay(dates[i], today)}>
                {d}
                {dates[i] && <span>{dates[i].getDate()}</span>}
              </styled.DayHead>
            ))}
          </styled.GridHour>

          {/* Рядки часу */}
          {timeSlots.map((slot, rowIdx) => (
            <styled.GridHour key={rowIdx}>
              <styled.HourLabel data-hour={slot.minute === 0 ? slot.hour : undefined}>{slot.label}</styled.HourLabel>

              {daysOfWeek.map((_, dayIdx) => {
                // Відбираємо уроки, що починаються в цьому слоті
                const lessonsInCell = lessons.filter(lesson =>
                  lesson.day === dayIdx &&
                  lesson.hour === slot.hour &&
                  lesson.minute >= slot.minute &&
                  lesson.minute < slot.minute + interval
                );

                const slotInfo = getSlotInfo(dayIdx, slot);

                return (
                  <styled.GridCell
                    key={dayIdx}
                    onClick={() => handleCellClick(dayIdx, slot)}
                    $slot={!!slotInfo}
                    $today={dates[dayIdx] && sameDay(dates[dayIdx], today)}
                  >
                    {/* Вільне вікно: підпис і кнопка видалення */}
                    {slotInfo && (
                      <>
                        {!lessonsInCell.length && (
                          <styled.SlotLabel>{slotInfo.type === "new-student" ? "Для нового учня" : "Вільно"}</styled.SlotLabel>
                        )}
                        <styled.DeleteSlotButton
                          title="Прибрати вікно"
                          onClick={e => handleDeleteSlot(dayIdx, slot, e)}
                        >
                          <LuX size={13} />
                        </styled.DeleteSlotButton>
                      </>
                    )}

                    {/* Карточки уроків */}
                    {lessonsInCell.map(lesson => {
                      // обчислюємо відступ від початку слоту:
                      const offsetPercent =
                        ((lesson.minute - slot.minute) / interval) * 100;

                      return (
                        <styled.LessonCard
                          key={`${lesson.name}-${lesson.time}`}
                          onClick={e => {
                            e.stopPropagation();
                            if (lesson.type === "cancel") return;
                            handleLessonClick(lesson);
                            setSelectedCell(null);
                            setSelectedLesson?.(lesson);
                          }}
                          style={{ top: `${offsetPercent}%` }}
                          $c={colorOf(lesson.type)}
                          $cancel={lesson.type === "cancel"}
                        >
                          <styled.StudentName>{lesson.name}</styled.StudentName>
                          <styled.LessonTime>{lesson.time}</styled.LessonTime>
                        </styled.LessonCard>
                      );
                    })}
                  </styled.GridCell>
                );
              })}
            </styled.GridHour>
          ))}
        </styled.ScheduleGrid>
      </styled.Scroller>
    </styled.Card>
  );
}

export default ScheduleGrid;
